import { useId, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import type { KpiTargetAuditRow } from '../../../types/strategicKpis';
import { SCOPE_TYPE_LABEL, SOURCE_LABEL, formatKpiValue } from '../../../utils/strategicKpiFormat';
import { formatDateTimeCH } from '../../../utils/format';
import { SidePanel } from '../shell/SidePanel';

interface Props {
    /** The audit rows the page loaded (same service call as the old TargetAuditTable); the panel makes no request. */
    rows: KpiTargetAuditRow[];
    loading: boolean;
    year: number;
    allYears: boolean;
    onToggleAllYears: (all: boolean) => void;
    onRefresh: () => void;
    unitOf: (kpiKey: string) => string;
    onClose: () => void;
}

const LABEL_SIZE = { fontSize: 'var(--fs-label)', lineHeight: 'var(--lh-label)' } as const;

type Kind = 'created' | 'changed' | 'removed';

/** created = no old value, removed = no new value, else changed. */
export function auditKind(r: Pick<KpiTargetAuditRow, 'OldValue' | 'NewValue'>): Kind {
    return r.OldValue === null ? 'created' : r.NewValue === null ? 'removed' : 'changed';
}

/** Newest first; equal timestamps by id descending. Does not mutate the input. */
export function sortAuditNewestFirst(rows: KpiTargetAuditRow[]): KpiTargetAuditRow[] {
    return [...rows].sort((a, b) => {
        const t = b.ChangedAt.localeCompare(a.ChangedAt);
        return t !== 0 ? t : b.Id - a.Id;
    });
}

/**
 * The change log of the targets in the side panel (masterplan skpi-redesign, package C4): one item per value
 * change, newest first, with KPI, scope, old -> new, who, when and source. Replaces TargetAuditTable (removed in D4).
 * Mounted = open; the page (C5) owns the panel state. The list scrolls in the panel body.
 */
export function AuditPanel({ rows, loading, year, allYears, onToggleAllYears, onRefresh, unitOf, onClose }: Props) {
    const sorted = useMemo(() => sortAuditNewestFirst(rows), [rows]);
    const allId = useId();

    return (
        <SidePanel
            title="Change log"
            sub={`Targets ${allYears ? 'all years' : year} · newest first · ${sorted.length} changes (latest 500)`}
            onClose={onClose}
            contentKey="audit"
            testId="skpi-audit-panel"
            closeLabel="Close change log"
        >
            <div className="flex items-center justify-between gap-3">
                <label htmlFor={allId} className="inline-flex cursor-pointer items-center gap-2" style={LABEL_SIZE}>
                    <input id={allId} type="checkbox" className="check" checked={allYears} onChange={e => onToggleAllYears(e.target.checked)} data-testid="audit-all-years" />
                    all years
                </label>
                <span className="inline-flex items-center gap-2">
                    {loading && <Loader2 className="i sm animate-spin" aria-hidden="true" />}
                    <button type="button" className="lnk" onClick={onRefresh} title="Reload the change log">Refresh</button>
                </span>
            </div>
            {sorted.length === 0 ? (
                <p className="muted" style={LABEL_SIZE}>{loading ? 'Loading…' : 'No changes recorded.'}</p>
            ) : (
                <ul className="m-0 flex list-none flex-col p-0" data-testid="audit-list">
                    {sorted.map(r => {
                        const unit = unitOf(r.KpiKey);
                        const kind = auditKind(r);
                        const oldText = r.OldValue === null ? 'none' : formatKpiValue(unit, r.OldValue);
                        const newText = r.NewValue === null ? 'removed' : formatKpiValue(unit, r.NewValue);
                        return (
                            <li key={r.Id} className="flex flex-col gap-0.5 py-2.5" style={{ borderBottom: '1px solid var(--hair)' }} data-testid="audit-row">
                                <span className="strong">
                                    {r.ScopeLabel} · {r.KpiName ?? r.KpiKey}
                                </span>
                                <span className="muted" style={LABEL_SIZE}>
                                    {SCOPE_TYPE_LABEL[r.ScopeType] ?? r.ScopeType}
                                    {allYears ? ` · ${r.TargetYear}` : ''}
                                </span>
                                <span className="num strong">
                                    {kind === 'created' ? `${newText} (new)` : kind === 'removed' ? `${oldText} → removed` : `${oldText} → ${newText}`}
                                </span>
                                <span className="muted" style={LABEL_SIZE}>
                                    {formatDateTimeCH(r.ChangedAt)} · {r.ChangedBy} · {SOURCE_LABEL[r.Source] ?? r.Source}
                                </span>
                                {r.Note && <span className="muted" style={LABEL_SIZE}>{r.Note}</span>}
                            </li>
                        );
                    })}
                </ul>
            )}
        </SidePanel>
    );
}
